import React, { useEffect, useRef } from 'react';
import { ChevronLeft, X } from 'lucide-react';
import { t } from '../../i18n';
import { useVisualViewport } from '../../utils/visualViewport';

function scrollTargetIntoScroller(scroller, target) {
  if (!scroller || !target || typeof target.getBoundingClientRect !== 'function') return;
  const s = scroller.getBoundingClientRect();
  const t = target.getBoundingClientRect();
  if (s.height <= 0) return;
  if (t.height <= s.height) {
    if (t.top < s.top) scroller.scrollTop -= (s.top - t.top);
    else if (t.bottom > s.bottom) scroller.scrollTop += (t.bottom - s.bottom);
    return;
  }
  scroller.scrollTop += (t.top - s.top);
}

// Centered card on the visual viewport. Safari's bottom toolbar and the iOS
// keyboard sit outside visualViewport, so a layout-viewport `inset-0` sheet
// would hide the focused field and the footer button. Never a bottom sheet.
// Warm ink/40 backdrop so the cream card has a clear boundary on a cream base.
export default function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  dismissible = true,
  compact = false,
  zClass = 'z-50',
  testId,
  headerExtra,
  onBack,
}) {
  const vv = useVisualViewport();
  const bodyRef = useRef(null);
  const keyboardOpen = vv.keyboardOpen;
  const tight = keyboardOpen && !compact;

  useEffect(() => {
    if (!open || !keyboardOpen) return undefined;
    const scroller = bodyRef.current;
    const sync = () => {
      const target = scroller?.querySelector('input:focus, textarea:focus');
      if (scroller && target) scrollTargetIntoScroller(scroller, target);
    };
    sync();
    const id = requestAnimationFrame(sync);
    return () => cancelAnimationFrame(id);
  }, [open, keyboardOpen, vv.height, vv.offsetTop]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (dismissible && e.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, dismissible, onClose]);

  if (!open) return null;

  return (
    <div
      className={`fixed left-0 right-0 ${zClass} flex flex-col items-center justify-center overflow-hidden pl-[max(1rem,env(safe-area-inset-left))] pr-[max(1rem,env(safe-area-inset-right))] ${keyboardOpen ? 'py-1' : 'py-4'}`}
      style={{ top: vv.offsetTop, height: `${vv.height}px` }}
      role="dialog"
      aria-modal="true"
      data-testid={testId}
    >
      <div
        className="absolute inset-0 bg-ink/40 transition-opacity"
        onClick={dismissible ? onClose : undefined}
        data-testid={testId ? `${testId}-backdrop` : undefined}
      />
      <div
        className={`relative w-full max-w-md max-h-full min-h-0 overflow-hidden bg-cream rounded-2xl border border-line shadow-2xl flex flex-col ${compact ? 'text-[14px]' : ''}`}
        data-testid={testId ? `${testId}-card` : undefined}
      >
        <div className={`modal-header flex-none flex items-center gap-2 ${compact ? (keyboardOpen ? 'px-4 pt-2 pb-1' : 'px-4 pt-3 pb-2') : tight ? 'px-5 pt-2 pb-1' : 'px-5 pt-4 pb-3'}`}>
          {onBack && (
            <button
              type="button"
              onClick={onBack}
              aria-label={t('common.back')}
              className="-ml-2 p-2 text-mute hover:text-ink transition-colors flex-shrink-0"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
          )}
          <h2 className={`min-w-0 flex-1 truncate font-medium text-ink leading-tight ${compact ? 'text-[16px]' : 'text-[17px]'}`}>{title}</h2>
          {headerExtra}
          {dismissible && (
            <button
              onClick={onClose}
              aria-label={t('common.close')}
              className="-mr-2 p-2 text-mute hover:text-ink transition-colors flex-shrink-0"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
        <div
          ref={bodyRef}
          className={`modal-body min-h-0 flex-1 overflow-y-auto ${compact ? 'px-4 pb-3' : tight ? 'px-5 pb-2' : 'px-5 pb-5'}`}
          onFocus={(e) => {
            const scroller = e.currentTarget;
            const target = e.target;
            requestAnimationFrame(() => scrollTargetIntoScroller(scroller, target));
          }}
        >
          {children}
        </div>
        {footer && (
          <div
            className={`flex-none border-t border-line-2 flex items-center gap-3 justify-end ${keyboardOpen ? (compact ? 'px-4 py-2' : 'px-5 py-2') : compact ? 'px-4 py-3' : 'px-5 py-4'}`}
            data-testid={testId ? `${testId}-footer` : undefined}
          >
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
